import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LandingHeader } from '@/components/landing/LandingHeader'
import { LandingDemoReader } from '@/components/landing/LandingDemoReader'
import { LandingDemoSavedNudge } from '@/components/landing/LandingDemoSavedNudge'
import { LandingCapabilitiesSection } from '@/components/landing/LandingCapabilitiesSection'
import { LandingFinalCta } from '@/components/landing/LandingFinalCta'
import { LandingProductShell } from '@/components/landing/LandingProductShell'
import { GuestPageBackground } from '@/components/layout/GuestPageBackground'
import { PublicFooter } from '@/components/layout/PublicFooter'
import { useGuestLocale, useLandingCopy } from '@/hooks/useGuestLocale'
import { applyDocumentLang, getGuestLocale } from '@/lib/guestLocale'
import { trackFunnel } from '@/lib/funnel'

export function LandingPage() {
  const copy = useLandingCopy()
  const locale = useGuestLocale()
  const [lookedUpWords, setLookedUpWords] = useState<string[]>([])
  const magicTracked = useRef(false)

  useEffect(() => {
    applyDocumentLang(locale)
  }, [locale])

  useEffect(() => {
    trackFunnel('LANDING_VIEW', { path: '/', locale: getGuestLocale() })
  }, [])

  const handleLookup = useCallback((word: string) => {
    trackFunnel('DEMO_WORD_TAPPED', { word: word.toLowerCase() })
    setLookedUpWords((prev) => {
      const lower = word.toLowerCase()
      if (prev.includes(lower)) return prev
      return [...prev, lower]
    })
  }, [])

  const showSavedNudge = useMemo(
    () => lookedUpWords.length >= 2,
    [lookedUpWords.length],
  )

  useEffect(() => {
    if (!showSavedNudge || magicTracked.current) return
    magicTracked.current = true
    trackFunnel('MAGIC_SEEN', { words: lookedUpWords })
  }, [showSavedNudge, lookedUpWords])

  return (
    <div className="relative min-h-screen text-foreground">
      <GuestPageBackground />
      <LandingHeader />

      <main className="relative">
        <section
          id="demo"
          className="relative mx-auto max-w-4xl px-4 pb-12 pt-10 sm:px-6 sm:pt-14 sm:pb-16"
        >
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-balance text-4xl font-semibold tracking-tight text-foreground sm:text-5xl sm:leading-[1.15]">
              {copy.heroTitle}
            </h1>
            <div className="mt-5 space-y-1">
              {copy.heroSubtitleLines.map((line, i) => (
                <p
                  key={line}
                  className={
                    i === 0
                      ? 'text-base text-foreground/90 sm:text-lg'
                      : 'text-sm text-muted-foreground sm:text-base'
                  }
                >
                  {line}
                </p>
              ))}
            </div>
          </div>

          <div className="relative mt-8 sm:mt-10">
            <LandingDemoReader onLookup={handleLookup} />
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground/90">
            {copy.demoHint}
            <span className="mx-1.5 text-muted-foreground/50">·</span>
            {copy.demoTapHint}
          </p>

          {showSavedNudge && <LandingDemoSavedNudge words={lookedUpWords} />}
        </section>

        <div className="relative pb-14 sm:pb-20">
          <div
            className="pointer-events-none absolute inset-x-0 -top-16 h-32 bg-gradient-to-b from-transparent to-slate-50/80 sm:-top-20 sm:h-40"
            aria-hidden
          />

          <LandingProductShell>
            <LandingCapabilitiesSection />
            <LandingFinalCta />
          </LandingProductShell>
        </div>

        <PublicFooter
          faqLabel={copy.footerFaq}
          privacyLabel={copy.footerPrivacy}
          termsLabel={copy.footerTerms}
        />
      </main>
    </div>
  )
}
